import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { suApiSlice } from 'src/redux/slices/api/su';
import { useAppDispatch } from 'src/redux/hooks';
import { LearningModulesCarousel } from 'src/components/carousel';
import { LearningModuleQueryType } from 'src/types/api/learningModule';
import { useClientId, useSelLocale } from 'src/contexts/ClientContext';
import { KatSpinner, KatPopover } from '@amzn/katal-react';
import { SuIcon } from '@amzn/seller-university-ui-components';
import ClientLogger from 'src/utils/logger';
import { ClientLog } from 'src/constants/clientLog';
import ErrorView from 'src/components/ErrorView';

interface SubtopicsSectionProps {
  topicId: string;
  subTopicList: Array<{
    subtopicId: string;
    title: string;
    description?: string;
  }>;
  registerElement?: (id: string, element: HTMLElement | null) => void;
}

const SubtopicsSection: React.FC<SubtopicsSectionProps> = ({ topicId, subTopicList, registerElement }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const clientId = useClientId();
  const selLocale = useSelLocale();
  const [isLoadingModules, setIsLoadingModules] = useState(false);
  const [subtopicsWithModules, setSubtopicsWithModules] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    const subscriptions: Array<ReturnType<ReturnType<typeof suApiSlice.endpoints.listLearningModules.initiate>>> = [];

    if (subTopicList?.length) {
      const loadAllModules = async () => {
        setIsLoadingModules(true);
        try {
          const subs = subTopicList.map((subtopic) => dispatch(
            suApiSlice.endpoints.listLearningModules.initiate({
              clientId,
              selLocale,
              queryPattern: {
                queryType: LearningModuleQueryType.SubTopic,
                subtopicQuery: { subtopicId: subtopic.subtopicId },
              },
            }),
          ));
          subscriptions.push(...subs);

          const results = await Promise.all(subs.map((s) => s.unwrap().catch(() => null)));

          if (cancelled) return;

          const subtopicsWithData = subTopicList
            .filter((_, index) => {
              const result = results[index];
              return result && result.modules && result.modules.length > 0;
            })
            .map((subtopic) => subtopic.subtopicId);

          setSubtopicsWithModules(subtopicsWithData);
        } finally {
          if (!cancelled) setIsLoadingModules(false);
        }
      };

      loadAllModules();
    }

    return () => {
      cancelled = true;
      subscriptions.forEach((s) => s.unsubscribe());
    };
  }, [topicId, subTopicList, dispatch, clientId, selLocale]);

  if (isLoadingModules) {
    return (
      <div className="flex min-h-[15rem] items-center justify-center">
        <KatSpinner />
      </div>
    );
  }

  if (subtopicsWithModules.length === 0) {
    if (subTopicList?.length) {
      ClientLogger.info(ClientLog.IllegalInternalDataState, {
        topicId,
        message: 'Topic has no associated learning modules',
        path: location.pathname,
      });
    }
    return <ErrorView />;
  }

  return (
    <div>
      {subTopicList
        ?.filter((subtopic) => subtopicsWithModules.includes(subtopic.subtopicId))
        .map((subtopic) => (
          <section
            key={subtopic.subtopicId}
            ref={registerElement ? (el) => registerElement(`subtopic-${subtopic.subtopicId}`, el) : undefined}
            className="mb-su-space-08"
          >
            <div className="mb-su-space-04 flex items-center gap-su-space-03">
              <h2 className="!text-su-heading-400 !font-bold">{subtopic.title}</h2>
              {subtopic.description && (
                <KatPopover
                  position="top-start"
                  triggerType="click"
                  variant="azure"
                >
                  <div slot="trigger" className="mb-su-space-02">
                    <SuIcon
                      icon="info"
                      size="xsmall"
                      className="cursor-pointer"
                    />
                  </div>
                  {subtopic.description}
                </KatPopover>
              )}
            </div>
            <LearningModulesCarousel subtopicId={subtopic.subtopicId} />
          </section>
        ))}
    </div>
  );
};

export default SubtopicsSection;
